import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import type { CommunityMember } from "@circle-react/types";

import { UserImage } from "@/react/components/SettingsApp/CompassCompat/UserImage";

export interface CommunityMemberOption {
  label: string;
  member: CommunityMember;
  prefix?: React.ReactNode;
  value: number | string;
}

export interface SelectedCommunityMember {
  avatarUrl: string;
  email: string;
  id: number | string;
  name: string;
}

interface UseCommunityMemberSelectOptionsParams {
  communityMembers: CommunityMember[];
  getCommunityMemberText?: (member: CommunityMember) => string;
  valueField?: "id" | "user_id";
}

const defaultGetText = (member: CommunityMember) => member.name ?? "";

interface BuildCommunityMemberOptionsParams {
  communityMembers: CommunityMember[];
  getCommunityMemberText?: (member: CommunityMember) => string;
  isAdmin: boolean;
  valueField?: "id" | "user_id";
}

export const buildCommunityMemberOptions = ({
  communityMembers,
  getCommunityMemberText = defaultGetText,
  isAdmin,
  valueField = "id",
}: BuildCommunityMemberOptionsParams): CommunityMemberOption[] =>
  (communityMembers ?? []).map(member => {
    const label = isAdmin
      ? `${getCommunityMemberText(member)} (${member.email})`
      : getCommunityMemberText(member);

    return {
      value: member[valueField] ?? member.id,
      label,
      member,
      prefix: (
        <UserImage
          src={member.avatar_url}
          name={member.name ?? ""}
          size="5"
          isFullRounded
        />
      ),
    };
  });

export const toSelectedCommunityMember = (
  option: CommunityMemberOption,
): SelectedCommunityMember => ({
  id: option.value,
  name: option.member.name || option.member.email || String(option.value),
  email: option.member.email ?? "",
  avatarUrl: option.member.avatar_url ?? "",
});

export const useCommunityMemberSelectOptions = ({
  communityMembers,
  getCommunityMemberText = defaultGetText,
  valueField = "id",
}: UseCommunityMemberSelectOptionsParams) => {
  const { currentCommunityMember } = usePunditUserContext();
  const isAdmin = isCommunityAdmin(currentCommunityMember);

  const options = buildCommunityMemberOptions({
    communityMembers,
    getCommunityMemberText,
    valueField,
    isAdmin,
  });

  return { options };
};
